import { FormEvent, ReactNode, useMemo, useState } from 'react';
import { Input, Select, Checkbox, Button } from '../atoms';

/**
 * Form — schema-driven form renderer.
 *
 * Derived from M3 Text Fields + Forms spec per Agent Protocol step 2:
 *   - Spec:      https://m3.material.io/components/text-fields/specs
 *                (M3 has no canonical "Form" component; the pattern is
 *                composed from labeled Text Fields + grouping headers)
 *   - Reference: Material Web (none for forms; uses individual mdc-* fields)
 *
 * Populus adaptations applied:
 *   - Consumes the `fields` shape returned by `GET /api/schema/:table`
 *     (Phase 11 Block 2 PR 1) — single source of truth for entity shape
 *   - Section grouping per `field.section`; sorted within section by
 *     `field.displayOrder` (M3 doesn't formalize sections; Populus needs
 *     them for 60-table CRUD where each entity has 15-30 fields)
 *   - Conditional show/hide per `field.conditional.showWhen` — re-evaluates
 *     on every value change; hidden fields keep their value in state but
 *     aren't submitted (consumer can filter on submit)
 *   - Field types dispatched by `field.dataType`:
 *       boolean → Checkbox atom (with label on right)
 *       integer/number → Input type=number
 *       date → Input type=date
 *       timestamp → Input type=datetime-local
 *       text with enumOptions → Select atom
 *       text otherwise → Input type=text
 *   - FK fields: if consumer passes `fkOptions[fieldName]`, render as
 *     Select; otherwise render as plain text input with a helper line
 *     ("FK to <table>"). Form doesn't fetch FK options itself
 *     (decoupling from data fetching — consumer can wire TanStack Query
 *     and pass a hash of options)
 *   - `immutable: true` + `mode='edit'` disables the field
 *   - Submit button row sticky at the bottom right (M3 Dialog footer
 *     pattern; matches the entity-form interaction shape)
 *
 * Token discipline:
 *   - Section headers, helper text, error text → --color-ink* (system)
 *   - Required-field asterisk → --color-status-red (semantic system token)
 *   - Submit button inherits Button atom --brand-primary
 *
 * Phase 11 Block 6 PR 6 (2026-05-12).
 */

export interface SchemaField {
  name: string;
  type: string;                                  // raw PG type (preserved for backwards compat)
  required: boolean;
  defaultValue: string | null;
  pk: boolean;
  enum: string[] | null;
  fk: { table: string; column: string } | null;
  label: string;
  section: string | null;
  displayOrder: number;
  immutable: boolean;
  hidden: boolean;
  conditional: { showWhen: Record<string, string | number | boolean> } | null;
  enumOptions: Array<{ value: string; label: string }> | null;
  dataType: 'boolean' | 'integer' | 'number' | 'date' | 'timestamp' | 'text';
}

export type FormValues = Record<string, unknown>;

export interface FormProps {
  fields: SchemaField[];
  initialValues?: FormValues;
  mode?: 'create' | 'edit';
  fkOptions?: Record<string, Array<{ value: string; label: string }>>;
  errors?: Record<string, string>;
  submitLabel?: string;
  onSubmit: (values: FormValues) => void | Promise<void>;
  onCancel?: () => void;
  submitting?: boolean;
}

export function Form({
  fields,
  initialValues = {},
  mode = 'create',
  fkOptions = {},
  errors = {},
  submitLabel,
  onSubmit,
  onCancel,
  submitting = false,
}: FormProps) {
  const visibleFields = useMemo(
    () => fields.filter(f => !f.hidden && !f.pk),
    [fields],
  );

  // Group by section, preserving section order from displayOrder of the
  // first field in each section
  const sections = useMemo(() => {
    const sectionMap = new Map<string, SchemaField[]>();
    for (const f of visibleFields) {
      const key = f.section ?? '_unsectioned';
      if (!sectionMap.has(key)) sectionMap.set(key, []);
      sectionMap.get(key)!.push(f);
    }
    for (const [, fs] of sectionMap) {
      fs.sort((a, b) => a.displayOrder - b.displayOrder);
    }
    return Array.from(sectionMap.entries()).map(([label, fields]) => ({
      label: label === '_unsectioned' ? null : label,
      fields,
      sortKey: fields[0]?.displayOrder ?? 0,
    })).sort((a, b) => a.sortKey - b.sortKey);
  }, [visibleFields]);

  const [values, setValues] = useState<FormValues>(() => {
    const next: FormValues = { ...initialValues };
    for (const f of fields) {
      if (next[f.name] === undefined && f.defaultValue !== null) {
        next[f.name] = f.defaultValue;
      }
    }
    return next;
  });

  function setValue(name: string, value: unknown) {
    setValues(prev => ({ ...prev, [name]: value }));
  }

  function isVisible(field: SchemaField): boolean {
    if (!field.conditional) return true;
    for (const [depField, expected] of Object.entries(field.conditional.showWhen)) {
      if (values[depField] !== expected) return false;
    }
    return true;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    await onSubmit(values);
  }

  const submitText = submitLabel ?? (mode === 'edit' ? 'Save changes' : 'Create');

  return (
    <form onSubmit={handleSubmit}>
      {sections.map((section, i) => (
        <FormSectionView
          key={i}
          label={section.label}
          fields={section.fields.filter(isVisible)}
          values={values}
          errors={errors}
          mode={mode}
          fkOptions={fkOptions}
          setValue={setValue}
        />
      ))}

      <footer style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 'var(--sp-2)',
        marginTop: 'var(--sp-6)',
        paddingTop: 'var(--sp-4)',
        borderTop: '1px solid var(--color-border)',
      }}>
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
        )}
        <Button type="submit" variant="primary" loading={submitting} disabled={submitting}>
          {submitText}
        </Button>
      </footer>
    </form>
  );
}

interface FormSectionViewProps {
  label: string | null;
  fields: SchemaField[];
  values: FormValues;
  errors: Record<string, string>;
  mode: 'create' | 'edit';
  fkOptions: Record<string, Array<{ value: string; label: string }>>;
  setValue: (name: string, value: unknown) => void;
}

function FormSectionView({
  label,
  fields,
  values,
  errors,
  mode,
  fkOptions,
  setValue,
}: FormSectionViewProps) {
  if (fields.length === 0) return null;
  return (
    <section style={{ marginBottom: 'var(--sp-5)' }}>
      {label && (
        <h3 style={{
          margin: '0 0 var(--sp-3)',
          fontSize: 'var(--fs-sm)',
          fontWeight: 'var(--fw-semibold)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--ls-wider)',
          color: 'var(--color-ink-3)',
        }}>{label}</h3>
      )}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: 'var(--sp-4)',
      }}>
        {fields.map(field => (
          <FieldView
            key={field.name}
            field={field}
            value={values[field.name]}
            error={errors[field.name]}
            disabled={mode === 'edit' && field.immutable}
            fkOptions={fkOptions[field.name]}
            onChange={v => setValue(field.name, v)}
          />
        ))}
      </div>
    </section>
  );
}

interface FieldViewProps {
  field: SchemaField;
  value: unknown;
  error?: string;
  disabled?: boolean;
  fkOptions?: Array<{ value: string; label: string }>;
  onChange: (value: unknown) => void;
}

function FieldView({ field, value, error, disabled, fkOptions, onChange }: FieldViewProps) {
  const labelNode: ReactNode = (
    <>
      {field.label}
      {field.required && (
        <span aria-hidden="true" style={{
          color: 'var(--color-status-red)',
          marginLeft: 2,
        }}>*</span>
      )}
    </>
  );

  // Checkbox renders its own label on the right; everything else uses
  // the standard label-above pattern
  if (field.dataType === 'boolean') {
    return (
      <div>
        <Checkbox
          checked={!!value}
          onChange={e => onChange(e.target.checked)}
          disabled={disabled}
          label={labelNode}
        />
        {error && <FieldError text={error} />}
      </div>
    );
  }

  return (
    <label style={{ display: 'block' }}>
      <span style={{
        display: 'block',
        marginBottom: 'var(--sp-1)',
        fontSize: 'var(--fs-sm)',
        fontWeight: 'var(--fw-medium)',
        color: 'var(--color-ink)',
      }}>{labelNode}</span>
      <FieldControl
        field={field}
        value={value}
        disabled={disabled}
        invalid={!!error}
        fkOptions={fkOptions}
        onChange={onChange}
      />
      {field.fk && !fkOptions && (
        <div style={{
          marginTop: 'var(--sp-0-5)',
          fontSize: 'var(--fs-xs)',
          color: 'var(--color-ink-3)',
        }}>FK → {field.fk.table}</div>
      )}
      {error && <FieldError text={error} />}
    </label>
  );
}

interface FieldControlProps {
  field: SchemaField;
  value: unknown;
  disabled?: boolean;
  invalid?: boolean;
  fkOptions?: Array<{ value: string; label: string }>;
  onChange: (value: unknown) => void;
}

function FieldControl({ field, value, disabled, invalid, fkOptions, onChange }: FieldControlProps) {
  const strVal = value === null || value === undefined ? '' : String(value);

  // FK with provided options → Select
  if (field.fk && fkOptions) {
    return (
      <Select
        value={strVal}
        disabled={disabled}
        invalid={invalid}
        onChange={e => onChange(e.target.value || null)}
        options={fkOptions}
        placeholder={`Select ${field.label.toLowerCase()}…`}
      />
    );
  }

  // Enum → Select
  if (field.enumOptions && field.enumOptions.length > 0) {
    return (
      <Select
        value={strVal}
        disabled={disabled}
        invalid={invalid}
        onChange={e => onChange(e.target.value || null)}
        options={field.enumOptions}
        placeholder={`Select ${field.label.toLowerCase()}…`}
      />
    );
  }

  // Number, date, timestamp → typed inputs
  let inputType: string = 'text';
  if (field.dataType === 'integer' || field.dataType === 'number') inputType = 'number';
  else if (field.dataType === 'date') inputType = 'date';
  else if (field.dataType === 'timestamp') inputType = 'datetime-local';

  return (
    <Input
      type={inputType}
      value={strVal}
      disabled={disabled}
      invalid={invalid}
      required={field.required}
      onChange={e => onChange(e.target.value === '' ? null : e.target.value)}
    />
  );
}

function FieldError({ text }: { text: string }) {
  return (
    <div style={{
      marginTop: 'var(--sp-1)',
      fontSize: 'var(--fs-sm)',
      color: 'var(--color-status-red)',
    }}>{text}</div>
  );
}
